import React, { useState, useContext, useMemo } from 'react';
import { AuthContext } from '../App';
import { formatCurrency, formatNumber, formatPercentage } from '../utils/formatters';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Search, 
  Plus, 
  Play, 
  Settings, 
  LogOut, 
  Activity, 
  TrendingUp, 
  Users, 
  Zap,
  Clock,
  CheckCircle,
  AlertTriangle,
  BarChart3,
  Target,
  DollarSign,
  Truck,
  FileText,
  UserCheck,
  ArrowRight,
  Filter,
  MoreVertical,
  Eye,
  Package,
  Cog,
  Store,
  Trash2,
  Lightbulb,
  Building,
  Key,
  Loader2,
  Book
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Button } from './ui/button';
import { Input } from './ui/input';
import ReportViewerModal from './ReportViewerModal';
import KPIComparisonPanel from './KPIComparisonPanel';
import UserManualModule from './UserManualModule';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import AgentExecutionDialog from './AgentExecutionDialog';
import Agent00Management from './Agent00Management';
import AgentSelectorModal from './AgentSelectorModal';
import ChangePasswordModal from './ChangePasswordModal';
import CompanyProfileSetup from './CompanyProfileSetup';
import IndustryProfilePanel from './IndustryProfilePanel';
import ThemeToggle from './ThemeToggle';
import GovernanceV2Panel from './governance_v2/GovernanceV2Panel';
import { IADSelector } from './iad/IADSelector';
import { ShieldCheck, FolderKanban, History as HistoryIcon, Radar, Crown, BookMarked } from 'lucide-react';
import { HistoricalPanel } from './historical/HistoricalPanel';
import { MaterialityPanel, MaterialityBadge } from './materiality/MaterialityPanel';
import { AttentionPanel, AttentionBadge, AttentionSummaryCard } from './attention/AttentionPanel';
import { MemoryPanel } from './memory/MemoryPanel';
import { ProjectsPanel } from './execution/ProjectsPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompanyDashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('mission-control');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showExecuteDialog, setShowExecuteDialog] = useState(false);
  const [showCreateThread, setShowCreateThread] = useState(false);
  const [newThread, setNewThread] = useState({
    query: '',
    thread_type: 'analysis',
    iad_codes: []
  });
  const [showCreateKPI, setShowCreateKPI] = useState(false);
  const [newKPI, setNewKPI] = useState({
    kpi_name: '',
    kpi_description: '',
    target_value: '',
    metric_type: 'number',
    period: 'monthly',
    module_type: '',
    agent_id: null,
    chart_type: 'line',
    show_in_dashboard: true
  });
  const [selectedThreadForReport, setSelectedThreadForReport] = useState(null);

  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedInsightForReport, setSelectedInsightForReport] = useState(null);
  const [showInsightModal, setShowInsightModal] = useState(false);
  const [showCreateInsight, setShowCreateInsight] = useState(false);
  const [latestJob, setLatestJob] = useState(null);
  const [loadingLatestJob, setLoadingLatestJob] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [profileStatus, setProfileStatus] = useState(null);

  const queryClient = useQueryClient();

  // Check profile status on mount
  React.useEffect(() => {
    const checkProfileStatus = async () => {
      try {
        const response = await axios.get(`${API}/companies/${user.company_id}/company-profile/status`);
        setProfileStatus(response.data);
        
        // Don't auto-show modal - let IndustryProfilePanel handle it
      } catch (error) {
        console.error('Error checking profile status:', error);
      }
    };
    
    if (user?.company_id) {
      checkProfileStatus();
    }
  }, [user?.company_id]);

  const handleProfileSetupComplete = async (result) => {
    // Refresh profile status
    try {
      const response = await axios.get(`${API}/companies/${user.company_id}/company-profile/status`);
      setProfileStatus(response.data);
    } catch (error) {
      console.error('Error refreshing profile status:', error);
    }
    
    // Close modal
    setShowProfileSetup(false);
    
    // Small delay to ensure backend has updated the data
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Refresh industry profile panel and other relevant queries
    // Use specific company ID to ensure correct cache invalidation
    const companyId = user?.company_id;
    if (companyId) {
      queryClient.invalidateQueries({ queryKey: ['master-context', companyId] });
      queryClient.invalidateQueries({ queryKey: ['master-context'] }); // Also invalidate generic key
      queryClient.invalidateQueries({ queryKey: ['dashboard', companyId] });
    }
    
    // Show success notification with new score if available
    if (result?.confidence_score) {
      toast.success(`Perfil actualizado. Nuevo score de confianza: ${result.confidence_score}%`);
    } else if (result?.classification) {
      toast.success(`Perfil de industria actualizado: ${result.classification.primary_industry || 'Clasificado'}`);
    }
  };

  // Queries
  const { data: dashboard } = useQuery({
    queryKey: ['dashboard', user?.company_id],
    queryFn: async () => {
      const response = await axios.get(`${API}/companies/${user.company_id}/dashboard`);
      return response.data;
    },
    enabled: !!user?.company_id
  });

  const { data: modulesRaw = [] } = useQuery({
    queryKey: ['company-modules', user?.company_id],
    queryFn: async () => {
      const response = await axios.get(`${API}/companies/${user.company_id}/modules`);
      return response.data;
    },
    enabled: !!user?.company_id
  });

  // Sort modules in specific order: Revenue, Supply Chain, Logistics, Operations, Finance, HR
  const modules = React.useMemo(() => {
    const moduleOrder = {
      'revenue': 1,
      'supply_chain': 2,
      'logistica': 3,
      'logistics': 3,
      'operaciones': 4,
      'operations': 4,
      'finanzas': 5,
      'finance': 5,
      'rrhh': 6,
      'human_resources': 6,
    };
    
    return [...modulesRaw].sort((a, b) => {
      const orderA = moduleOrder[a.module_type?.toLowerCase()] || 999;
      const orderB = moduleOrder[b.module_type?.toLowerCase()] || 999;
      return orderA - orderB;
    });
  }, [modulesRaw]);

  const { data: agentsRaw = [] } = useQuery({
    queryKey: ['company-agents', user?.company_id],
    queryFn: async () => {
      const response = await axios.get(`${API}/companies/${user.company_id}/agents`);
      return response.data;
    },
    enabled: !!user?.company_id
  });

  // Sort agents by number (ascending)
  const agents = React.useMemo(() => {
    const extractAgentNumber = (name) => {
      const match = name?.match(/(\d{3})/);
      return match ? parseInt(match[1], 10) : 999;
    };
    
    return [...agentsRaw].sort((a, b) => {
      const numA = extractAgentNumber(a.name);
      const numB = extractAgentNumber(b.name);
      return numA - numB;
    });
  }, [agentsRaw]);

  const { data: threads = [] } = useQuery({
    queryKey: ['threads', user?.company_id],
    queryFn: async () => {
      const response = await axios.get(`${API}/companies/${user.company_id}/threads`);
      return response.data;
    },
    enabled: !!user?.company_id,
    // Poll every 5 seconds to check for completed generations
    refetchInterval: 5000
  });

  // Check if there are any pending threads that need polling
  const hasPendingThreads = React.useMemo(() => {
    return threads.some(t => t.status === 'pending' || t.status === 'in_progress');
  }, [threads]);


  const { data: insightsRaw = [] } = useQuery({
    queryKey: ['insights-reports', user?.company_id],
    queryFn: async () => {
      const response = await axios.get(`${API}/companies/${user.company_id}/insights-reports`);
      return response.data.insights || [];
    },
    enabled: !!user?.company_id,
    // Poll every 5 seconds to get new insights
    refetchInterval: 5000
  });

  // Filter out "governance" insights (strategic routes) - they should only appear in Mission Control
  const insights = React.useMemo(() => {
    return insightsRaw.filter(insight => insight.source_type !== 'governance');
  }, [insightsRaw]);


  const { data: executions = [] } = useQuery({
    queryKey: ['executions', user?.company_id],
    queryFn: async () => {
      const response = await axios.get(`${API}/companies/${user.company_id}/executions`);
      return response.data;
    },
    enabled: !!user?.company_id
  });

  const { data: kpis = [] } = useQuery({
    queryKey: ['company-kpis', user?.company_id],
    queryFn: async () => {
      const response = await axios.get(`${API}/companies/${user.company_id}/kpis`);
      const kpisData = response.data;
      
      // Fetch historical data for each KPI
      const kpisWithHistory = await Promise.all(
        kpisData.map(async (kpi) => {
          try {
            const historyResponse = await axios.get(
              `${API}/companies/${user.company_id}/kpis/${kpi.id}/historical-data`
            );
            return {
              ...kpi,
              historical_data: historyResponse.data.historical_data || []
            };
          } catch (error) {
            console.log(`No historical data for KPI ${kpi.kpi_name}`);
            return kpi;
          }
        })
      );
      
      return kpisWithHistory;
    },
    enabled: !!user?.company_id
  });

  // Mutations
  const executeAgentMutation = useMutation({
    mutationFn: async ({ agentId, inputData }) => {
      const response = await axios.post(`${API}/companies/${user.company_id}/agents/${agentId}/execute`, {
        input_data: inputData
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['executions']);
      queryClient.invalidateQueries(['dashboard']);
      queryClient.invalidateQueries(['insights-reports']);
      toast.success('Agente ejecutado exitosamente - Insight generado');
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al ejecutar agente');
      throw error; // Re-throw to handle in component
    }
  });

  const createThreadMutation = useMutation({
    mutationFn: async (threadData) => {
      const response = await axios.post(
        `${API}/companies/${user.company_id}/threads`,
        threadData
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['threads', user.company_id]);
      setShowCreateThread(false);
      setNewThread({ query: '', thread_type: 'analysis', iad_codes: [] });
      toast.success('Ruta estratégica creada exitosamente');
    },
    onError: (error) => {
      console.error('Error creating thread:', error);
      let errorMessage = 'Error al crear ruta estratégica';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Handle validation errors (array of objects)
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } 
        // Handle string errors
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // Handle object errors
        else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    }
  });

  // Delete thread mutation
  const deleteThreadMutation = useMutation({
    mutationFn: async (threadId) => {
      const response = await axios.delete(
        `${API}/companies/${user.company_id}/threads/${threadId}`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['threads', user.company_id]);
      toast.success('Ruta estratégica eliminada exitosamente');
    },
    onError: (error) => {
      console.error('Error deleting thread:', error);
      let errorMessage = 'Error al eliminar ruta estratégica';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    }
  });

  // Handle delete with confirmation
  const handleDeleteThread = (threadId, threadQuery) => {
    if (window.confirm(`¿Estás seguro de que deseas eliminar la ruta estratégica "${threadQuery}"?\n\nEsta acción no se puede deshacer y también eliminará todas las insights asociadas.`)) {
      deleteThreadMutation.mutate(threadId);
    }
  };

  const handleCreateNewInsight = async () => {
    setLoadingLatestJob(true);
    try {
      const response = await axios.get(
        `${API}/companies/${user.company_id}/ingestion-jobs/latest`
      );
      
      setLatestJob(response.data);
      setShowCreateInsight(true);
    } catch (error) {
      console.error('Error loading latest job:', error);
      if (error.response?.status === 404) {
        toast.error('No hay datos de ingesta disponibles. Primero sube archivos en "Ingesta de Datos".');
      } else {
        toast.error('Error al cargar datos de ingesta');
      }
    } finally {
      setLoadingLatestJob(false);
    }
  };


  // Delete insight mutation
  const deleteInsightMutation = useMutation({
    mutationFn: async (insightId) => {
      const response = await axios.delete(
        `${API}/companies/${user.company_id}/insights/${insightId}`
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['insights-reports', user.company_id]);
      toast.success('Insight eliminado exitosamente');
    },
    onError: (error) => {
      console.error('Error deleting insight:', error);
      let errorMessage = 'Error al eliminar insight';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    }
  });



  const createKPIMutation = useMutation({
    mutationFn: async (kpiData) => {
      const response = await axios.post(`${API}/companies/${user.company_id}/kpis`, kpiData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['company-kpis', user.company_id]);
      setShowCreateKPI(false);
      setNewKPI({
        kpi_name: '',
        kpi_description: '',
        target_value: '',
        metric_type: 'number',
        period: 'monthly',
        module_type: '',
        agent_id: null,
        chart_type: 'line',
        show_in_dashboard: true
      });
      toast.success('KPI creado exitosamente');
    },
    onError: (error) => {
      console.error('Error creating KPI:', error);
      let errorMessage = 'Error al crear KPI';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Handle validation errors (array of objects)
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } 
        // Handle string errors
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // Handle object errors
        else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    }
  });

  const deleteKPIMutation = useMutation({
    mutationFn: async (kpiId) => {
      const response = await axios.delete(`${API}/companies/${user.company_id}/kpis/${kpiId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['company-kpis', user.company_id]);
      toast.success('KPI eliminado');
    },
    onError: (error) => {
      console.error('Error deleting KPI:', error);
      let errorMessage = 'Error al eliminar KPI';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    }
  });

  // Separate recent threads (< 24hrs) from historical ones (> 24hrs)
  const now = new Date();
  const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  
  const recentThreads = threads.filter(thread => {
    const createdAt = new Date(thread.created_at);
    return createdAt > twentyFourHoursAgo;
  });
  
  const historicalThreads = threads.filter(thread => {
    const createdAt = new Date(thread.created_at);
    return createdAt <= twentyFourHoursAgo;
  });

  // Helper function to get module icon
  const getModuleIcon = (moduleType) => {
    const iconMap = {
      'revenue': DollarSign,
      'supply_chain': Truck,
      'operations': Cog,
      'logistics': Package,
      'finance': BarChart3,
      'hr': Users
    };
    return iconMap[moduleType] || BarChart3;
  };

  // Helper function to get module name
  const getModuleName = (moduleType) => {
    const nameMap = {
      'revenue': 'Revenue Intelligence',
      'supply_chain': 'Supply Chain Intelligence',
      'operations': 'Operations Intelligence',
      'logistics': 'Logistics Intelligence',
      'finance': 'Finance Intelligence',
      'hr': 'HR Intelligence'
    };
    return nameMap[moduleType] || moduleType;
  };

  // Removed: generateKPIChartData - Only showing real data now

  // Group KPIs by module (only those marked to show in dashboard AND have real data)
  const kpisByModule = {};
  const enabledModules = modules.filter(m => m.is_enabled);
  
  enabledModules.forEach(module => {
    const moduleKPIs = kpis.filter(kpi => 
      kpi.module_type === module.module_type && 
      kpi.is_active && 
      kpi.show_in_dashboard !== false &&
      kpi.historical_data && 
      kpi.historical_data.length > 0  // SOLO mostrar si hay datos reales
    );
    if (moduleKPIs.length > 0) {
      kpisByModule[module.module_type] = {
        module: module,
        kpis: moduleKPIs
      };
    }
  });

  const handleExecuteAgent = async (agentId, inputData) => {
    return executeAgentMutation.mutateAsync({ agentId, inputData });
  };

  const openExecuteDialog = (agent) => {
    setSelectedAgent(agent);
    setShowExecuteDialog(true);
  };

  // Mock investigations data
  const investigations = [
    {
      id: 1,
      title: "Q4 Sales Pipeline Analysis",
      status: "In Progress",
      progress: 78,
      team: "SI MI ET",
      completion: "78% complete",
      timeAgo: "2 minutes ago",
      advisors: 3,
      description: "Analyzing 23% increase in qualified leads but conversion rate dropping..."
    },
    {
      id: 2,
      title: "Supply Chain Resilience Study", 
      status: "In Progress",
      progress: 62,
      team: "EI IW",
      completion: "62% complete", 
      timeAgo: "8 minutes ago",
      advisors: 2,
      description: "Analyzing supplier risk profiles and delivery performance..."
    },
    {
      id: 3,
      title: "Board Deck Preparation",
      status: "Ready for Review",
      progress: 95,
      team: "DI IW", 
      completion: "95% complete",
      timeAgo: "15 minutes ago",
      advisors: 3,
      description: "Financial summary and strategic initiatives overview completed..."
    },
    {
      id: 4,
      title: "Production Efficiency Deep Dive",
      status: "In Progress", 
      progress: 54,
      team: "DI IW",
      completion: "54% complete",
      timeAgo: "35 minutes ago", 
      advisors: 2,
      description: "Investigating OEE improvements across manufacturing lines..."
    }
  ];

  // Memoize the thread prop passed to ReportViewerModal for insights.
  // Without this, a new object was created on every parent render (5s refetchInterval +
  // followUp input keystrokes) causing the modal's useEffect to fire repeatedly and
  // triggering React Error #185 ("Maximum update depth exceeded").
  const insightThreadProp = useMemo(() => {
    if (!selectedInsightForReport) return null;
    return {
      id: selectedInsightForReport.id,
      company_id: selectedInsightForReport.company_id,
      query: selectedInsightForReport.agent_name || selectedInsightForReport.query,
      content: selectedInsightForReport.content,
      structured_report: selectedInsightForReport.structured_report,
      created_at: selectedInsightForReport.created_at,
      source_type: selectedInsightForReport.source_type || 'agent00',
      conversation_history: selectedInsightForReport.conversation_history || [],
      charts: selectedInsightForReport.charts || []
    };
  }, [selectedInsightForReport]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex">
      {/* Sidebar */}
      <div className="w-64 sidebar border-r border-blue-500/20">
        <div className="sidebar-content">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                <Activity className="w-4 h-4 text-white" />
              </div>
              <h1 className="text-lg font-bold vector-logo">VECTOR STRATEGY</h1>
            </div>
            
            <nav className="space-y-1">
              <div className="text-xs text-slate-400 uppercase tracking-wide mb-3 px-3">Core</div>

              {user?.role !== 'staff' && (
                <button
                  onClick={() => setActiveTab('attention')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm sidebar-item ${
                    activeTab === 'attention' ? 'active' : 'text-slate-300'
                  }`}
                  data-testid="attention-tab"
                >
                  <Crown className="w-4 h-4 flex-shrink-0 text-amber-400" />
                  <span className="truncate" title="Prioridad Ejecutiva">Prioridad Ejecutiva</span>
                  <AttentionBadge companyId={user?.company_id} />
                </button>
              )}
              
              <button
                onClick={() => setActiveTab('mission-control')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm sidebar-item ${
                  activeTab === 'mission-control' ? 'active' : 'text-slate-300'
                }`}
                data-testid="mission-control-tab"
              >
                <Target className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Mission Control</span>
              </button>
              
              {/* Hide Threads from STAFF users */}
              {user?.role !== 'staff' && (
                <button
                  onClick={() => setActiveTab('threads')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm sidebar-item ${
                    activeTab === 'threads' ? 'active' : 'text-slate-300'
                  }`}
                  data-testid="threads-tab"
                >
                  <Activity className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">Ruta Estratégica</span>
                </button>
              )}
              
              {user?.role !== 'staff' && (
                <button
                  onClick={() => setActiveTab('governance-v2')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm sidebar-item ${
                    activeTab === 'governance-v2' ? 'active' : 'text-slate-300'
                  }`}
                  data-testid="governance-v2-tab"
                >
                  <ShieldCheck className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">Decisiones Gobernadas</span>
                </button>
              )}

              <button
                onClick={() => setActiveTab('projects')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm sidebar-item ${
                  activeTab === 'projects' ? 'active' : 'text-slate-300'
                }`}
                data-testid="projects-tab"
              >
                <FolderKanban className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Proyectos</span>
              </button>

              <button
                onClick={() => setActiveTab('materiality')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm sidebar-item ${
                  activeTab === 'materiality' ? 'active' : 'text-slate-300'
                }`}
                data-testid="materiality-tab"
              >
                <Radar className="w-4 h-4 flex-shrink-0 text-red-400" />
                <span className="truncate" title="Alertas y Oportunidades">Alertas</span>
                <MaterialityBadge companyId={user?.company_id} />
              </button>

              {user?.role !== 'staff' && (
                <button
                  onClick={() => setActiveTab('historical')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm sidebar-item ${
                    activeTab === 'historical' ? 'active' : 'text-slate-300'
                  }`}
                  data-testid="historical-tab"
                >
                  <HistoryIcon className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">Inteligencia Histórica</span>
                </button>
              )}

              <button
                onClick={() => setActiveTab('memory')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm sidebar-item ${
                  activeTab === 'memory' ? 'active' : 'text-slate-300'
                }`}
                data-testid="memory-tab"
              >
                <BookMarked className="w-4 h-4 flex-shrink-0 text-indigo-400" />
                <span className="truncate" title="Memoria Institucional">Memoria Institucional</span>
              </button>

              {/* Hide Insights from STAFF users */}
              {user?.role !== 'staff' && (
                <button
                  onClick={() => setActiveTab('insights')}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm sidebar-item ${
                    activeTab === 'insights' ? 'active' : 'text-slate-300'
                  }`}
                >
                  <TrendingUp className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">Inteligencia Ejecutiva</span>
                </button>
              )}
              
              <button
                onClick={() => setActiveTab('industry-profile')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm sidebar-item ${
                  activeTab === 'industry-profile' ? 'active' : 'text-slate-300'
                }`}
              >
                <Building className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Perfil de Industria</span>
                {profileStatus && !profileStatus.has_profile && (
                  <span className="ml-auto w-2 h-2 bg-yellow-400 rounded-full"></span>
                )}
              </button>

              <button
                onClick={() => setActiveTab('user-manual')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm sidebar-item ${
                  activeTab === 'user-manual' ? 'active' : 'text-slate-300'
                }`}
              >
                <Book className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Manual de Usuario</span>
              </button>

              <button
                onClick={() => setActiveTab('agent00')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm sidebar-item ${
                  activeTab === 'agent00' ? 'active' : 'text-slate-300'
                }`}
              >
                <Package className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">Ingesta de Datos</span>
              </button>

              <div className="text-xs text-slate-400 uppercase tracking-wide mb-3 px-3 mt-6">Modules</div>
              
              {modules.filter(m => m.is_enabled).length === 0 && (
                <div className="px-3 py-2 text-xs text-slate-500">No modules enabled</div>
              )}
              
              {modules.filter(m => m.is_enabled).map((module) => {
                const moduleIcons = {
                  revenue: DollarSign,
                  supply_chain: Truck,
                  operaciones: Settings,
                  logistica: Truck,
                  finanzas: BarChart3,
                  rrhh: Users
                };
                const ModuleIcon = moduleIcons[module.module_type] || Zap;
                
                return (
                  <button
                    key={module.id}
                    onClick={() => setActiveTab(module.module_type)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm sidebar-item ${
                      activeTab === module.module_type ? 'active' : 'text-slate-300'
                    }`}
                    style={{ whiteSpace: 'normal' }}
                  >
                    <ModuleIcon className="w-4 h-4 flex-shrink-0" />
                    <span style={{ color: '#ffffff', fontSize: '14px' }} className="flex-1 text-left">{module.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        <div className="sidebar-footer">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-medium text-white">
                {user?.full_name?.charAt(0) || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          
          <div className="mb-3">
            <ThemeToggle />
          </div>
          
          <Button
            onClick={() => setShowChangePassword(true)}
            variant="ghost"
            size="sm"
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-700 mb-2"
          >
            <Key className="w-4 h-4 mr-2" />
            Cambiar Contraseña
          </Button>
          
          <Button
            onClick={logout}
            variant="ghost"
            size="sm"
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-700"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <>
        {/* Mission Control Tab */}
        {activeTab === 'mission-control' && (
          <div className="mission-control">
            <div className="p-8 bg-background">
              {/* Header */}
              <div className="mb-8">
                <h1 className="text-4xl font-bold text-foreground mb-2">Mission Control</h1>
                <p className="text-muted-foreground text-lg">Centro de comando de inteligencia de negocios</p>
              </div>
              {user?.role !== 'staff' && <AttentionSummaryCard companyId={user?.company_id} onOpen={() => setActiveTab('attention')} />}

              {/* Info Box */}
              <div className="relative mb-8">
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-500/20 rounded-lg p-3 flex-shrink-0">
                      <Plus className="w-6 h-6 text-blue-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        Para crear un análisis, haz clic en &quot;Nueva Ruta Estratégica&quot;
                      </h3>
                      <p className="text-muted-foreground">
                        Una ruta estratégica es una conversación con tus asesores de IA. Puedes preguntar cualquier cosa sobre tu negocio:
                        &quot;¿Por qué cayeron las ventas el mes pasado?&quot; o &quot;Ayúdame a preparar la reunión de junta directiva&quot;
                      </p>
                    </div>
                    <Button 
                      className="btn-gradient flex-shrink-0" 
                      data-testid="new-thread-btn"
                      onClick={() => setShowCreateThread(true)}
                      size="lg"
                    >
                      <Plus className="w-5 h-5 mr-2" />
                      Nueva Ruta Estratégica
                    </Button>
                  </div>
                </div>
              </div>

              {/* KPI Benchmark Comparisons */}
              <div className="mb-8">
                <KPIComparisonPanel 
                  companyId={user?.company_id} 
                  onNavigateToThread={(thread) => {
                    // After creating thread from KPI, refresh threads and optionally show report
                    queryClient.invalidateQueries(['threads', user?.company_id]);
                    toast.success('Ruta Estratégica creada desde el KPI');
                  }}
                />
              </div>

              {/* Ruta Estratégica Activa */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-foreground">Ruta Estratégica Activa</h2>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <span className="text-sm">Últimas 24 horas</span>
                    {historicalThreads.length > 0 && (
                      <Badge className="badge-info ml-2">
                        {historicalThreads.length} en historial
                      </Badge>
                    )}
                  </div>
                </div>

                {recentThreads.length === 0 ? (
                  <Card className="glass-dark border-blue-500/20">
                    <CardContent className="p-8 text-center">
                      <Activity className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                      <h3 className="text-lg font-medium text-foreground mb-2">No hay rutas estratégicas activas</h3>
                      <p className="text-muted-foreground mb-4">
                        Crea tu primera ruta estratégica para comenzar a analizar tu negocio
                      </p>
                      <Button 
                        className="btn-gradient"
                        onClick={() => setShowCreateThread(true)}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Crear Ruta
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {recentThreads.map((thread) => (
                      <Card key={thread.id} className="investigation-card card-hover">
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h3 className="font-semibold text-white mb-1">{thread.query}</h3>
                              <p className="text-sm text-slate-400 mb-3">
                                {thread.modules_consulted.length > 0 
                                  ? `Módulos consultados: ${thread.modules_consulted.join(', ')}`
                                  : 'Procesando...'
                                }
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge 
                                className={
                                  thread.status === 'completed' ? 'badge-success' :
                                  thread.status === 'in_progress' ? 'badge-info' :
                                  thread.status === 'pending' ? 'badge-warning' :
                                  'badge-error'
                                }
                              >
                                {thread.status === 'completed' ? 'Completado' :
                                 thread.status === 'in_progress' ? 'En proceso' :
                                 thread.status === 'pending' ? 'Pendiente' :
                                 'Error'}
                              </Badge>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 hover:bg-red-500/20 hover:text-red-400"
                                onClick={() => handleDeleteThread(thread.id, thread.query)}
                                disabled={deleteThreadMutation.isPending}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>

                          <div className="space-y-3">
                            <div className="flex items-center justify-between text-sm mb-3">
                              <span className="text-slate-400">
                                {new Date(thread.created_at).toLocaleDateString('es-ES')}
                              </span>
                              <Badge className="badge-warning">{thread.thread_type}</Badge>
                            </div>
                            
                            {thread.status === 'completed' && (
                              <Button 
                                className="w-full btn-gradient"
                                size="sm"
                                onClick={() => {
                                  setSelectedThreadForReport(thread);
                                  setShowReportModal(true);
                                }}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                Ver Reporte
                              </Button>
                            )}
                            
                            {thread.status === 'pending' && (
                              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
                                  <span className="text-amber-300 text-sm font-medium">Generando análisis...</span>
                                </div>
                                <p className="text-slate-400 text-xs mt-1">
                                  El Super Agente está procesando los datos
                                </p>
                              </div>
                            )}
                            
                            {thread.status === 'error' && (
                              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                                <p className="text-red-300 text-sm">Error en la generación</p>
                                <p className="text-slate-400 text-xs mt-1">
                                  {typeof thread.error === 'string' 
                                    ? thread.error 
                                    : typeof thread.error === 'object' 
                                      ? JSON.stringify(thread.error) 
                                      : 'Intenta crear una nueva ruta'}
                                </p>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Create Thread Dialog */}
        <Dialog open={showCreateThread} onOpenChange={setShowCreateThread}>
          <DialogContent className="glass-dark border-blue-500/20 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl">Crear Nueva Ruta Estratégica de Análisis</DialogTitle>
              <DialogDescription className="text-slate-400">
                El Super Agente analizará datos de todos tus módulos para responder tu consulta.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Tipo de Análisis
                </label>
                <Select
                  value={newThread.thread_type}
                  onValueChange={(value) => setNewThread({ ...newThread, thread_type: value })}
                >
                  <SelectTrigger className="form-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="analysis">📊 Análisis - Entender qué está pasando</SelectItem>
                    <SelectItem value="prediction">🔮 Predicción - Qué va a pasar</SelectItem>
                    <SelectItem value="projection">📈 Proyección - Qué podría pasar</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Tu Pregunta o Solicitud
                </label>
                <Textarea
                  value={newThread.query}
                  onChange={(e) => setNewThread({ ...newThread, query: e.target.value })}
                  placeholder="Ej: ¿Por qué disminuyeron las ventas en el último trimestre? o ¿Cuál será el revenue proyectado para el próximo mes?"
                  className="form-input min-h-[150px]"
                />
                <p className="text-xs text-slate-500 mt-2">
                  Sé lo más específico posible. El Super Agente analizará datos de todos tus módulos habilitados.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Metodologías IAD (hasta 3)
                </label>
                <IADSelector
                  companyId={user?.company_id}
                  query={newThread.query}
                  value={newThread.iad_codes}
                  onChange={(codes) => setNewThread({ ...newThread, iad_codes: codes })}
                  testidPrefix="thread-iad-selector"
                />
              </div>

              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                <p className="text-sm text-blue-300 mb-2">💡 Consejos para mejores resultados:</p>
                <ul className="text-xs text-slate-400 space-y-1">
                  <li>• Incluye períodos de tiempo específicos</li>
                  <li>• Menciona métricas o KPIs que te interesan</li>
                  <li>• Pregunta por relaciones entre módulos (ej: ventas y logística)</li>
                </ul>
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  onClick={() => setShowCreateThread(false)}
                  variant="ghost"
                  className="text-slate-300"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={() => {
                    if (!newThread.query.trim()) {
                      toast.error('Escribe tu pregunta o solicitud');
                      return;
                    }
                    createThreadMutation.mutate({ ...newThread, iad_codes: newThread.iad_codes.length ? newThread.iad_codes : null });
                  }}
                  className="btn-gradient"
                  disabled={createThreadMutation.isPending}
                >
                  {createThreadMutation.isPending ? 'Creando...' : 'Crear Ruta'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Threads History Tab */}
        {activeTab === 'threads' && (
          <div className="p-8">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-foreground mb-2">Historial de Ruta Estratégica</h1>
              <p className="text-muted-foreground">Todos los análisis realizados en tu empresa</p>
            </div>

            {threads.length === 0 ? (
              <Card className="glass-dark border-blue-500/20">
                <CardContent className="p-12 text-center">
                  <Activity className="w-16 h-16 mx-auto mb-4 text-slate-600" />
                  <h3 className="text-xl font-medium text-slate-300 mb-2">No hay rutas estratégicas</h3>
                  <p className="text-slate-400 mb-6">
                    Crea tu primera ruta estratégica en Mission Control para comenzar
                  </p>
                  <Button 
                    onClick={() => setActiveTab('mission-control')}
                    className="btn-gradient"
                  >
                    <Target className="w-4 h-4 mr-2" />
                    Ir a Mission Control
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Recent Threads Section */}
                {recentThreads.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-blue-400" />
                      Recientes (últimas 24 horas)
                    </h3>
                    <div className="space-y-3">
                      {recentThreads.map((thread) => (
                        <Card key={thread.id} className="glass-dark border-blue-500/20 hover:border-blue-500/40 transition-all">
                          <CardContent className="p-5">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-2">
                                  <h4 className="font-medium text-white truncate">{thread.query}</h4>
                                  <Badge className="badge-warning flex-shrink-0">{thread.thread_type}</Badge>
                                  <Badge 
                                    className={`flex-shrink-0 ${
                                      thread.status === 'completed' ? 'badge-success' :
                                      thread.status === 'in_progress' ? 'badge-info' :
                                      thread.status === 'pending' ? 'badge-warning' :
                                      'badge-error'
                                    }`}
                                  >
                                    {thread.status === 'completed' ? 'Completado' :
                                     thread.status === 'in_progress' ? 'En proceso' :
                                     thread.status === 'pending' ? 'Pendiente' :
                                     'Error'}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-slate-400">
                                  <span>{new Date(thread.created_at).toLocaleString('es-ES')}</span>
                                  {thread.modules_consulted && thread.modules_consulted.length > 0 && (
                                    <span className="truncate">
                                      Módulos: {thread.modules_consulted.join(', ')}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                                {thread.status === 'completed' && (
                                  <Button
                                    onClick={() => {
                                      setSelectedThreadForReport(thread);
                                      setShowReportModal(true);
                                    }}
                                    size="sm"
                                    className="btn-gradient"
                                  >
                                    <Eye className="w-4 h-4 mr-2" />
                                    Ver Reporte
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 hover:bg-red-500/20 hover:text-red-400"
                                  onClick={() => handleDeleteThread(thread.id, thread.query)}
                                  disabled={deleteThreadMutation.isPending}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Historical Threads Section */}
                {historicalThreads.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-slate-400" />
                      Históricos (más de 24 horas)
                    </h3>
                    <div className="space-y-3">
                      {historicalThreads.map((thread) => (
                        <Card key={thread.id} className="glass-dark border-slate-700 hover:border-slate-600 transition-all">
                          <CardContent className="p-5">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3 mb-2">
                                  <h4 className="font-medium text-slate-200 truncate">{thread.query}</h4>
                                  <Badge className="badge-warning flex-shrink-0">{thread.thread_type}</Badge>
                                  <Badge 
                                    className={`flex-shrink-0 ${
                                      thread.status === 'completed' ? 'badge-success' :
                                      thread.status === 'in_progress' ? 'badge-info' :
                                      thread.status === 'pending' ? 'badge-warning' :
                                      'badge-error'
                                    }`}
                                  >
                                    {thread.status === 'completed' ? 'Completado' :
                                     thread.status === 'in_progress' ? 'En proceso' :
                                     thread.status === 'pending' ? 'Pendiente' :
                                     'Error'}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-slate-400">
                                  <span>{new Date(thread.created_at).toLocaleString('es-ES')}</span>
                                  {thread.modules_consulted && thread.modules_consulted.length > 0 && (
                                    <span className="truncate">
                                      Módulos: {thread.modules_consulted.join(', ')}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                                {thread.status === 'completed' && (
                                  <Button
                                    onClick={() => {
                                      setSelectedThreadForReport(thread);
                                      setShowReportModal(true);
                                    }}
                                    size="sm"
                                    variant="outline"
                                    className="border-slate-600 text-slate-300 hover:bg-slate-800"
                                  >
                                    <Eye className="w-4 h-4 mr-2" />
                                    Ver Reporte
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-8 w-8 p-0 hover:bg-red-500/20 hover:text-red-400"
                                  onClick={() => handleDeleteThread(thread.id, thread.query)}
                                  disabled={deleteThreadMutation.isPending}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

    {/* Module Intelligence Views - Dynamic */}
        {modules.filter(m => m.is_enabled && activeTab === m.module_type).map((module) => {
          const moduleAgents = agents.filter(agent => agent.module_type === module.module_type);

          return (
            <div key={module.id} className="p-8">
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-foreground mb-2">{module.name}</h1>
                <p className="text-muted-foreground">Execute specialized agents for {module.name ? module.name.toLowerCase() : 'this module'}</p>
              </div>

              {moduleAgents.length === 0 ? (
                <Card className="glass-dark border-blue-500/20">
                  <CardContent className="p-8 text-center">
                    <Activity className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                    <h3 className="text-lg font-medium text-slate-300 mb-2">No agents available</h3>
                    <p className="text-slate-400">No agents have been assigned to this module yet.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {moduleAgents.map((agent) => (
                    <Card key={agent.id} className={`agent-card ${agent.is_enabled ? 'agent-enabled' : ''}`}>
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-white text-sm">{agent.name}</CardTitle>
                          <Badge className={agent.is_enabled ? 'badge-success' : 'badge-error'}>
                            {agent.is_enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-slate-300 text-sm mb-4">{agent.description || 'No description'}</p>
                        <div className="flex justify-between text-xs text-slate-400 mb-4">
                          <span>Usage: {agent.usage_count || 0}</span>
                          {agent.last_used && (
                            <span>Last: {new Date(agent.last_used).toLocaleDateString()}</span>
                          )}
                        </div>
                        
                        {agent.is_enabled && (
                          <Button
                            onClick={() => openExecuteDialog(agent)}
                            size="sm"
                            className="w-full btn-gradient"
                            data-testid={`execute-agent-${agent.name}`}
                          >
                            <Play className="w-3 h-3 mr-2" />
                            Execute
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Insights Tab */}
        {activeTab === 'insights' && (
          <div className="p-8">
            <div className="mb-8">
              <div className="flex justify-between items-center">
                <div>
                  <h1 className="text-3xl font-bold text-foreground mb-2">Inteligencia Ejecutiva</h1>
                  <p className="text-muted-foreground">Análisis automáticos generados por ejecuciones de agentes</p>
                </div>
                <Button 
                  onClick={handleCreateNewInsight}
                  className="btn-gradient"
                  disabled={loadingLatestJob}
                >
                  {loadingLatestJob ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Cargando...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Crear Nuevo Insight
                    </>
                  )}
                </Button>
              </div>
            </div>

            {insights.length === 0 ? (
              <Card className="glass-dark border-blue-500/20">
                <CardContent className="p-12 text-center">
                  <Lightbulb className="w-16 h-16 mx-auto mb-4 text-slate-600" />
                  <h3 className="text-xl font-medium text-slate-300 mb-2">No hay insights aún</h3>
                  <p className="text-slate-400 mb-6">
                    Los insights se generan automáticamente cuando ejecutas agentes. Ejecuta tu primer agente para ver insights aquí.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-yellow-400" />
                    Total: {insights.length} insights
                  </h3>
                </div>

                <div className="space-y-3">
                  {insights.map((insight) => {
                    const agentTypeBadge = {
                      'functional': { label: 'Funcional', class: 'badge-info' },
                      'premium': { label: 'Premium', class: 'badge-warning' },
                      'c-level': { label: 'C-Level', class: 'badge-success' }
                    };

                    const typeBadge = agentTypeBadge[insight.structured_report?.agent_type] || agentTypeBadge['functional'];

                    return (
                      <Card key={insight.id} className="glass-dark border-yellow-500/20 hover:border-yellow-500/40 transition-all">
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3 mb-2">
                                <Lightbulb className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                                <h4 className="font-medium text-white truncate">{insight.agent_name}</h4>
                                <Badge className={`flex-shrink-0 ${typeBadge.class}`}>
                                  {typeBadge.label}
                                </Badge>
                                <Badge className="badge-secondary flex-shrink-0">
                                  {insight.module_type}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-slate-400 mb-2">
                                <span>{new Date(insight.created_at).toLocaleString('es-ES')}</span>
                              </div>
                              
                              {/* Preview of content */}
                              <p className="text-sm text-slate-300 line-clamp-2">
                                {(() => {
                                  // Extract plain text from HTML
                                  const tempDiv = document.createElement('div');
                                  tempDiv.innerHTML = insight.content || '';
                                  const textContent = tempDiv.textContent || tempDiv.innerText || '';
                                  return textContent.substring(0, 150) + (textContent.length > 150 ? '...' : '');
                                })()}
                              </p>

                              {/* Related agents included */}
                              {insight.structured_report?.related_agents_included && 
                               insight.structured_report.related_agents_included.length > 0 && (
                                <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
                                  <Users className="w-3 h-3" />
                                  <span>Incluye análisis de: {insight.structured_report.related_agents_included.join(', ')}</span>
                                </div>
                              )}

                              {/* KPI Performance if available */}
                              {insight.structured_report?.kpi_comparison && (
                                <div className="mt-2 flex items-center gap-2">
                                  <Badge className="badge-info text-xs">
                                    KPI: {insight.structured_report.kpi_comparison.kpi_name} • {insight.structured_report.kpi_comparison.performance}
                                  </Badge>
                                </div>
                              )}
                            </div>

                            <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                              <Button
                                onClick={() => {
                                  setSelectedInsightForReport(insight);
                                  setShowInsightModal(true);
                                }}
                                size="sm"
                                className="btn-gradient"
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                Ver Insight
                              </Button>
                              
                              {/* Delete button - only for admins */}
                              {user && (user.role === 'super_admin' || user.role === 'admin') && (
                                <Button
                                  onClick={() => {
                                    if (window.confirm('¿Estás seguro de que deseas eliminar este insight? Esta acción no se puede deshacer.')) {
                                      deleteInsightMutation.mutate(insight.id);
                                    }
                                  }}
                                  size="sm"
                                  variant="destructive"
                                  disabled={deleteInsightMutation.isLoading}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Industry Profile Tab */}
        {activeTab === 'governance-v2' && user?.role !== 'staff' && (
          <GovernanceV2Panel companyId={user?.company_id} user={user} />
        )}

        {activeTab === 'memory' && (
          <div className="p-8">
            <MemoryPanel companyId={user?.company_id} role={user?.role} />
          </div>
        )}

        {activeTab === 'historical' && user?.role !== 'staff' && (
          <div className="p-8">
            <HistoricalPanel companyId={user?.company_id} />
          </div>
        )}

        {activeTab === 'attention' && user?.role !== 'staff' && (
          <div className="p-8">
            <AttentionPanel companyId={user?.company_id} userEmail={user?.email} onNavigate={setActiveTab} />
          </div>
        )}

        {activeTab === 'materiality' && (
          <div className="p-8">
            <MaterialityPanel companyId={user?.company_id} role={user?.role} />
          </div>
        )}

        {activeTab === 'projects' && (
          <div className="p-8">
            <ProjectsPanel companyId={user?.company_id} role={user?.role} currentUserId={user?.id} />
          </div>
        )}

        {activeTab === 'industry-profile' && (
          <div className="p-8">
            <IndustryProfilePanel 
              companyId={user?.company_id}
              userToken={localStorage.getItem('token')}
              onReconfigure={() => setShowProfileSetup(true)}
            />
          </div>
        )}

        {/* User Manual Tab */}
        {activeTab === 'user-manual' && (
          <div className="p-8">
            <UserManualModule userRole="tenant" />
          </div>
        )}

        {/* Agent00 Ingesta de Datos Tab */}
        {activeTab === 'agent00' && (
          <Agent00Management companyId={user?.company_id} userToken={localStorage.getItem('token')} />
        )}
        </>
      </div>

      {/* Execute Agent Dialog */}
      <AgentExecutionDialog
        isOpen={showExecuteDialog}
        onClose={() => {
          setShowExecuteDialog(false);
          setSelectedAgent(null);
        }}
        agent={selectedAgent}
        companyId={user?.company_id}
        onExecute={handleExecuteAgent}
      />

      {/* Report Viewer Modal */}
      <ReportViewerModal
        thread={selectedThreadForReport}
        open={showReportModal}
        onClose={() => {
          setShowReportModal(false);
          setSelectedThreadForReport(null);
        }}
      />


      {/* Insight Viewer Modal */}
      <ReportViewerModal
        thread={insightThreadProp}
        open={showInsightModal}
        onClose={() => {
          setShowInsightModal(false);
          setSelectedInsightForReport(null);
        }}
      />

      {/* Create New Insight Modal */}
      {latestJob && (
        <AgentSelectorModal
          isOpen={showCreateInsight}
          onClose={() => {
            setShowCreateInsight(false);
            setLatestJob(null);
          }}
          jobId={latestJob.id}
          companyId={user?.company_id}
          moduleType={latestJob.module_type}
          userToken={user?.token}
          onSuccess={() => {
            queryClient.invalidateQueries(['insights-reports', user?.company_id]);
            setShowCreateInsight(false);
            setLatestJob(null);
          }}
        />
      )}

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        userToken={user?.token}
      />

      {/* Company Profile Setup Modal */}
      <CompanyProfileSetup
        isOpen={showProfileSetup}
        onClose={() => setShowProfileSetup(false)}
        companyId={user?.company_id}
        onComplete={handleProfileSetupComplete}
      />

    </div>
  );
};

export default CompanyDashboard;